import type { PointData, TextStyle } from 'pixi.js';
import { Text, MeshRope, RenderTexture } from 'pixi.js';
import MathUtils from '../../../../utils/MathUtils';

export interface CurvedTextSettings {
  readonly textStyle: Partial<TextStyle>;
  readonly text: string;
  readonly radius: number;
  readonly padding?: number;
}

export class CurvedText extends MeshRope {
  // can be used to manipulate mesh.
  public readonly points: PointData[];

  constructor(config: CurvedTextSettings) {
    const { textStyle, text, radius, padding = 5 } = config;

    const { rt, textWidth } = CurvedText.createRenderTexture(textStyle, text, padding);
    const pointCount = Math.ceil(textWidth / 10);
    const points: PointData[] = CurvedText.generateCurvePoints(radius, textWidth, pointCount);

    super({ texture: rt, points });

    this.points = points;
  }

  public destroy(): void {
    this.parent?.removeChild(this);
    this.autoUpdate = false;
    super.destroy({ textureSource: true, children: true });
  }

  private static generateCurvePoints(radius: number, textWidth: number, pointCount: number): PointData[] {
    const perimeter = 2 * Math.PI * radius;
    const textPermiterRatio = textWidth / perimeter;
    const points: PointData[] = [];
    const centerOffsetAngle = -(Math.PI * textPermiterRatio) - Math.PI / 2;
    for (let i = 0; i < pointCount; i += 1) {
      const angle = 2 * Math.PI * textPermiterRatio * ((i + 0.5) / pointCount) + centerOffsetAngle;
      points.push(MathUtils.getPointOnCircle(0, radius, radius, angle));
    }
    return points;
  }

  private static createRenderTexture(
    textStyle: Partial<TextStyle>,
    text: string,
    padding: number,
  ): { rt: RenderTexture; textWidth: number } {
    const textField = new Text(text, textStyle);
    const textWidth = textField.width;

    textField.props({
      x: padding,
      y: padding,
    });

    const rt = RenderTexture.create({
      width: textField.width + padding * 2,
      height: textField.height + padding * 2,
    });

    textField.pixi.renderer.render(textField, { renderTexture: rt });
    textField.destroy();

    return { rt, textWidth };
  }
}
